import type { LottieJson, LottieSlotInfo, LottieSlotType, LottieSlotValue } from './types';

/** rgba 0–1 for a CSS token string, or null when unresolvable. */
export type TokenResolver = (cssValue: string) => [number, number, number, number] | null;

function slotType(p: unknown): { type: LottieSlotType; value: LottieSlotValue } {
  if (typeof p === 'string') return { type: 'text', value: p };
  const k = p && typeof p === 'object' ? (p as { k?: unknown }).k : undefined;
  if (typeof k === 'number') return { type: 'scalar', value: k };
  if (Array.isArray(k)) {
    // len-4 arrays are assumed to be [r,g,b,a] color values; anything else is a vec2.
    if (k.length === 4) return { type: 'color', value: [...(k as number[])] };
    return { type: 'vec2', value: [...(k as number[])] };
  }
  return { type: 'text', value: String(k ?? '') };
}

/** Slot ids + types + default values, derived from the document's slots table. */
export function listSlots(doc: LottieJson): LottieSlotInfo[] {
  return Object.entries(doc.slots ?? {}).map(([id, def]) => ({ id, ...slotType(def.p) }));
}

/** The animated-property node a slot value substitutes in for. */
function propertyNode(slotP: unknown, override: LottieSlotValue | undefined, resolveToken?: TokenResolver): unknown {
  // Deep-clone on every branch so the output never aliases the input slots table
  // (lottie-web mutates the JSON it's handed; slots re-resolve on theme/override change).
  if (override === undefined) return structuredClone(slotP);
  if (typeof override === 'string' && override.startsWith('var(')) {
    const rgba = resolveToken?.(override);
    return rgba ? { a: 0, k: [...rgba] } : structuredClone(slotP);
  }
  if (typeof slotP === 'string') return String(override); // text slot
  return { a: 0, k: Array.isArray(override) ? [...override] : override };
}

/**
 * Statically substitute slot (`sid`) references: each `{ sid }` property ref is
 * replaced with the slot's default from the top-level table, overlaid with
 * `overrides`. Pure — returns a deep copy; the input is never mutated. Unknown
 * sids (no table entry) pass through untouched. lottie-web never sees a sid.
 */
export function resolveLottieSlots(
  doc: LottieJson,
  overrides: Record<string, LottieSlotValue>,
  resolveToken?: TokenResolver,
): LottieJson {
  const slots = doc.slots ?? {};
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (!node || typeof node !== 'object') return node;
    const o = node as Record<string, unknown>;
    if (typeof o.sid === 'string' && slots[o.sid]) {
      return propertyNode(slots[o.sid].p, overrides[o.sid], resolveToken);
    }
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(o)) out[k] = walk(v);
    return out;
  };
  return walk(doc) as LottieJson;
}
